import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { redefinirSenha } from '../../api';

/* ─── Floating orb decoration ─── */
function Orb({ size, top, left, opacity, delay }) {
  return (
    <div
      style={{
        position: 'absolute',
        width: size,
        height: size,
        top,
        left,
        opacity,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%)',
        animation: `float 6s ease-in-out ${delay} infinite alternate`,
        pointerEvents: 'none',
      }}
    />
  );
}

function GridLines() {
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.06 }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.8" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
  );
}

const EyeOpen = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOff = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

export default function RedefinirSenha() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Link inválido. Solicite uma nova recuperação de senha.');
      return;
    }
    if (senha !== confirmarSenha) {
      setError('As senhas não coincidem. Verifique e tente novamente.');
      return;
    }
    if (senha.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }

    setLoading(true);
    try {
      await redefinirSenha(token, senha);
      setSucesso(true);
    } catch (err) {
      setError(err.message || 'Erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:wght@300;400;500;600&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; }

        @keyframes float {
          from { transform: translateY(0px) scale(1); }
          to   { transform: translateY(-24px) scale(1.06); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(32px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes successPop {
          0%   { opacity: 0; transform: scale(0.85) translateY(12px); }
          60%  { transform: scale(1.03) translateY(-2px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .red-root { display: flex; min-height: 100vh; font-family: 'DM Sans', sans-serif; }

        .left-panel {
          position: relative;
          overflow: hidden;
          flex: 1;
          display: none;
          flex-direction: column;
          justify-content: space-between;
          padding: 48px;
          background: linear-gradient(145deg, #3b0764 0%, #6b21a8 40%, #9d174d 100%);
          color: white;
        }
        @media (min-width: 900px) { .left-panel { display: flex; } }

        .left-logo {
          font-weight: 600; font-size: 1.25rem; letter-spacing: 0.04em;
          display: flex; align-items: center; gap: 8px;
          animation: fadeUp 0.5s ease both;
        }
        .left-logo span { font-weight: 300; opacity: 0.8; }

        .left-hero { animation: fadeUp 0.7s 0.2s ease both; opacity: 0; }
        .left-hero h1 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 3.5vw, 3rem);
          line-height: 1.15;
          margin-bottom: 16px;
        }
        .left-hero p { font-size: 0.95rem; opacity: 0.7; line-height: 1.6; max-width: 340px; }

        .right-panel {
          display: flex; align-items: center; justify-content: center;
          padding: 32px 24px; background: #fafafa; flex: none; width: 100%;
        }
        @media (min-width: 900px) { .right-panel { width: 440px; } }

        .form-card { width: 100%; max-width: 380px; animation: slideIn 0.6s 0.1s ease both; opacity: 0; }

        .form-title { font-family: 'Playfair Display', serif; font-size: 2rem; color: #1a1a2e; margin-bottom: 6px; }
        .form-subtitle { font-size: 0.875rem; color: #6b7280; margin-bottom: 32px; line-height: 1.5; }

        .field { margin-bottom: 20px; }
        .field label {
          display: block; font-size: 0.8rem; font-weight: 500; color: #374151;
          margin-bottom: 6px; letter-spacing: 0.02em;
        }
        .input-wrap { position: relative; }
        .field input {
          width: 100%; padding: 12px 16px; border: 1.5px solid #e5e7eb; border-radius: 10px;
          font-family: 'DM Sans', sans-serif; font-size: 0.9rem; color: #111; background: white;
          transition: border-color 0.2s, box-shadow 0.2s; outline: none;
        }
        .field input:focus { border-color: #9d174d; box-shadow: 0 0 0 3px rgba(157,23,77,0.1); }
        .field input:disabled { opacity: 0.6; cursor: not-allowed; }
        .field input.input-error { border-color: #f43f5e; box-shadow: 0 0 0 3px rgba(244,63,94,0.1); }
        .field input.input-success { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,0.08); }

        .toggle-pass {
          position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer; color: #9ca3af;
          display: flex; align-items: center; padding: 4px; transition: color 0.2s;
        }
        .toggle-pass:hover { color: #9d174d; }

        .error-box {
          background: #fff1f2; border: 1px solid #fecdd3; color: #be123c; font-size: 0.82rem;
          padding: 10px 14px; border-radius: 8px; margin-bottom: 16px;
          display: flex; align-items: center; gap: 8px;
        }

        .btn-primary {
          width: 100%; padding: 13px; border: none; border-radius: 10px;
          background: linear-gradient(135deg, #3b0764 0%, #9d174d 100%); color: white;
          font-family: 'DM Sans', sans-serif; font-size: 0.9rem; font-weight: 600; cursor: pointer;
          letter-spacing: 0.03em; transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 4px 14px rgba(157,23,77,0.35); margin-bottom: 12px;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .btn-primary:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(157,23,77,0.45); }
        .btn-primary:active:not(:disabled) { transform: translateY(0); }
        .btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }

        .spinner {
          width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white; border-radius: 50%; animation: spin 0.7s linear infinite;
        }

        .footer-note { text-align: center; font-size: 0.72rem; color: #9ca3af; margin-top: 28px; }

        .success-card { width: 100%; max-width: 380px; text-align: center; animation: successPop 0.55s ease both; }
        .success-icon-wrap {
          width: 72px; height: 72px; border-radius: 50%;
          background: linear-gradient(135deg, #3b0764, #9d174d);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 20px; box-shadow: 0 8px 24px rgba(157,23,77,0.35);
        }
        .success-title { font-family: 'Playfair Display', serif; font-size: 1.75rem; color: #1a1a2e; margin-bottom: 8px; }
        .success-subtitle { font-size: 0.875rem; color: #6b7280; line-height: 1.6; margin-bottom: 24px; }
      `}</style>

      <div className="red-root">
        {/* ══ LEFT PANEL ══ */}
        <div className="left-panel">
          <GridLines />
          <Orb size="320px" top="-80px" left="-80px" opacity={0.5} delay="0s" />
          <Orb size="200px" top="40%" left="60%" opacity={0.3} delay="2s" />
          <Orb size="140px" top="70%" left="-20px" opacity={0.25} delay="1s" />

          <div className="left-logo">
            <strong>YOUX</strong>
            <span>GROUP</span>
          </div>

          <div className="left-hero">
            <h1>Escolha uma<br />nova senha<br />segura.</h1>
            <p>
              Defina uma nova senha para voltar a acessar o painel financeiro com segurança.
            </p>
          </div>

          <div />
        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div className="right-panel">
          {sucesso ? (
            <div className="success-card">
              <div className="success-icon-wrap">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="success-title">Senha redefinida!</h2>
              <p className="success-subtitle">
                Sua senha foi alterada com sucesso. Agora você já pode entrar no painel com a nova senha.
              </p>
              <button className="btn-primary" onClick={() => navigate('/')}>
                Ir para o Login
              </button>
            </div>
          ) : (
            <div className="form-card">
              <h2 className="form-title">Nova senha</h2>
              <p className="form-subtitle">Escolha uma nova senha para sua conta.</p>

              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label>Nova senha</label>
                  <div className="input-wrap">
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      disabled={loading}
                      required
                      style={{ paddingRight: '42px' }}
                      className={
                        confirmarSenha && senha !== confirmarSenha
                          ? 'input-error'
                          : confirmarSenha && senha === confirmarSenha
                          ? 'input-success'
                          : ''
                      }
                    />
                    <button type="button" className="toggle-pass" onClick={() => setShowPass((v) => !v)}>
                      {showPass ? <EyeOff /> : <EyeOpen />}
                    </button>
                  </div>
                </div>

                <div className="field">
                  <label>Confirmar nova senha</label>
                  <div className="input-wrap">
                    <input
                      type={showConfirmPass ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      disabled={loading}
                      required
                      style={{ paddingRight: '42px' }}
                      className={
                        confirmarSenha && senha !== confirmarSenha
                          ? 'input-error'
                          : confirmarSenha && senha === confirmarSenha
                          ? 'input-success'
                          : ''
                      }
                    />
                    <button type="button" className="toggle-pass" onClick={() => setShowConfirmPass((v) => !v)}>
                      {showConfirmPass ? <EyeOff /> : <EyeOpen />}
                    </button>
                  </div>
                </div>

                {confirmarSenha && senha !== confirmarSenha && (
                  <p style={{ fontSize: '0.75rem', color: '#be123c', marginTop: '-10px', marginBottom: '16px' }}>
                    As senhas não coincidem.
                  </p>
                )}

                {error && (
                  <div className="error-box">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {error}
                  </div>
                )}

                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? (<><div className="spinner" /> Salvando...</>) : 'Redefinir senha'}
                </button>
              </form>

              <p className="footer-note">
                © {new Date().getFullYear()} Youx Group · Acesso restrito
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
